import { useState, useEffect } from 'react';
import { Copy, Send, Check, Loader2, Smartphone } from 'lucide-react';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import { api } from '../../../lib/api';
import { atendimentoApi } from '../lib/api';

// Convidar um contato pro aplicativo do cliente.
//
// O link e' gerado aqui e enviado pela rota de mensagem QUE JA EXISTE — nao ha
// caminho de envio proprio. Assim o convite sai pelo mesmo canal, com a mesma
// assinatura e sob as mesmas travas (inclusive a que impede documento de cliente
// sair pelo celular pessoal do Ruan). Um envio proprio seria um segundo lugar pra
// essas regras envelhecerem em silencio.
//
// ⚠️ O botao de COPIAR nao e' plano B decorativo: hoje o chip do Atendimento esta
// desligado, e enquanto estiver o envio pelo sistema falha. Copiar e mandar pelo
// aparelho e' o caminho que funciona — e a tela diz isso em vez de deixar a
// vendedora batendo num botao que nao vai.

interface Props {
  aberto: boolean;
  contatoId: number;
  conversaId: number;
  onFechar: () => void;
}

interface ConviteGerado {
  link: string;
  nome: string | null;
  expira_em: string;
  tem_ficha: boolean;
  mensagem_sugerida: string;
}

export function ModalConvidarApp({ aberto, contatoId, conversaId, onFechar }: Props) {
  const toast = useToast();
  const [convite, setConvite] = useState<ConviteGerado | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setConvite(null);
    setErro(null);
    setCopiado(false);
    setCarregando(true);
    // O link nasce ao ABRIR o modal, nao ao clicar em enviar: a vendedora precisa
    // poder ler o texto antes de mandar. Link gerado e nao usado so vence sozinho.
    api.post<ConviteGerado>('/api/app-convites', { contato_id: contatoId })
      .then(setConvite)
      .catch((e: { message?: string }) => setErro(e?.message ?? 'Nao deu para gerar o convite.'))
      .finally(() => setCarregando(false));
  }, [aberto, contatoId]);

  async function copiar() {
    if (!convite) return;
    try {
      await navigator.clipboard.writeText(convite.mensagem_sugerida);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      toast.erro('Nao deu para copiar. Selecione o texto e copie na mao.');
    }
  }

  async function enviar() {
    if (!convite) return;
    setEnviando(true);
    try {
      await atendimentoApi.enviarTexto(conversaId, { texto: convite.mensagem_sugerida });
      toast.sucesso('Convite enviado na conversa.');
      onFechar();
    } catch (e) {
      // Erro do canal (inbox fora do ar, janela fechada) vem com texto proprio —
      // mostra ele, que diz o que fazer, em vez de um "falhou" generico.
      toast.erro((e as { message?: string })?.message ?? 'Nao deu para enviar.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal aberto={aberto} titulo="Convidar para o aplicativo" onFechar={onFechar} largura={520}>
      <div className="p-4 space-y-3">
        {carregando && (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
            <Loader2 size={16} className="animate-spin" /> Gerando o link...
          </div>
        )}

        {erro && (
          <div className="text-sm rounded-lg p-3" style={{ backgroundColor: '#fef2f2', color: '#b91c1c' }}>
            {erro}
          </div>
        )}

        {convite && (
          <>
            <div className="text-sm rounded-lg p-3 flex gap-2" style={{ backgroundColor: 'var(--accent-soft, #fff8ef)', color: '#7c3d09' }}>
              <Smartphone size={16} className="flex-shrink-0 mt-0.5" />
              <div>
                O link vale <strong>7 dias</strong> e serve <strong>uma vez só</strong>. Ele já leva o
                número deste contato — o cliente só escolhe a senha.
                {!convite.tem_ficha && (
                  <div className="mt-1">
                    ⚠️ Este número ainda <strong>não tem ficha de cliente</strong>. O cadastro no app
                    funciona igual, mas o vínculo com a ficha fica pendente.
                  </div>
                )}
              </div>
            </div>

            <label className="block">
              <span className="text-xs text-gray-500">Mensagem que vai ser enviada</span>
              <textarea
                readOnly
                value={convite.mensagem_sugerida}
                rows={8}
                className="w-full mt-1 px-3 py-2 text-sm border rounded-lg no-upper"
                style={{ resize: 'none' }}
              />
            </label>

            <div className="flex gap-2 pt-1">
              <button
                onClick={enviar}
                disabled={enviando}
                className="flex-1 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {enviando ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                {enviando ? 'Enviando...' : 'Enviar nesta conversa'}
              </button>
              <button
                onClick={copiar}
                className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50 flex items-center gap-1.5"
              >
                {copiado ? <Check size={15} /> : <Copy size={15} />}
                {copiado ? 'Copiado' : 'Copiar'}
              </button>
            </div>

            <p className="text-xs text-gray-500">
              Se o envio pelo sistema falhar, copie e mande pelo celular — o link é o mesmo.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}
